import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Handshake, Plus, Pencil, Trash2, X, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

const TIPOS = [
  { value: 'aseguradora', label: 'Aseguradora' },
  { value: 'empresa', label: 'Empresa' },
  { value: 'institucion', label: 'Institución' },
  { value: 'descuento', label: 'Descuento' },
  { value: 'otro', label: 'Otro' },
];

const TIPO_COLOR: Record<string, string> = {
  aseguradora: 'bg-blue-50 text-blue-700',
  empresa: 'bg-orange-50 text-orange-700',
  institucion: 'bg-purple-50 text-purple-700',
  descuento: 'bg-green-50 text-green-700',
  otro: 'bg-gray-100 text-gray-600',
};

const emptyForm = { nombre: '', tipo: 'empresa', entidad: '', descuentoPorcentaje: '', vigenciaInicio: '', vigenciaFin: '', activo: true };
const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

export function ConveniosPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState(emptyForm);

  const { data: convenios = [], isLoading } = useQuery({
    queryKey: ['convenios'],
    queryFn: () => api.get('/api/convenios').then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (dto: any) => api.post('/api/convenios', dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['convenios'] }); toast.success('Convenio creado'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear convenio')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: any) => api.patch(`/api/convenios/${id}`, dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['convenios'] }); toast.success('Convenio actualizado'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar convenio')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/convenios/${id}`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['convenios'] }); toast.success('Convenio eliminado'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar convenio')),
  });

  function openCreate() { setEditing(null); setForm(emptyForm); setOpen(true); }
  function openEdit(c: any) {
    setEditing(c);
    setForm({
      nombre: c.nombre, tipo: c.tipo, entidad: c.entidad || '',
      descuentoPorcentaje: c.descuentoPorcentaje?.toString() || '',
      vigenciaInicio: c.vigenciaInicio ? c.vigenciaInicio.split('T')[0] : '',
      vigenciaFin: c.vigenciaFin ? c.vigenciaFin.split('T')[0] : '',
      activo: c.activo,
    });
    setOpen(true);
  }
  function closeModal() { setOpen(false); setEditing(null); }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const dto: any = {
      ...form,
      descuentoPorcentaje: form.descuentoPorcentaje ? parseFloat(form.descuentoPorcentaje) : null,
      vigenciaInicio: form.vigenciaInicio || null,
      vigenciaFin: form.vigenciaFin || null,
    };
    if (!dto.entidad) delete dto.entidad;
    if (editing) updateMutation.mutate({ id: editing.id, dto });
    else createMutation.mutate(dto);
  }

  const f = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [field]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-text-primary">Convenios</h2>
          <p className="text-sm text-text-secondary mt-0.5">{convenios.length} convenio{convenios.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openCreate} className="hidden md:flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
          <Plus size={15} /> Nuevo convenio
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-text-secondary">Cargando...</div>
      ) : convenios.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
          <Handshake size={48} className="mx-auto text-gray-200 mb-4" />
          <p className="font-medium text-text-primary">Sin convenios registrados</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
          {convenios.map((c: any) => (
            <div key={c.id} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50/50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-text-primary">{c.nombre}</p>
                  <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', TIPO_COLOR[c.tipo] || 'bg-gray-100 text-gray-600')}>
                    {TIPOS.find(t => t.value === c.tipo)?.label || c.tipo}
                  </span>
                  {c.activo ? (
                    <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle size={12} /> Activo</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-gray-400"><XCircle size={12} /> Inactivo</span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  {c.entidad && <span className="text-xs text-text-secondary">{c.entidad}</span>}
                  {c.descuentoPorcentaje && <span className="text-xs font-semibold text-green-600">{c.descuentoPorcentaje}% dto.</span>}
                  {c.vigenciaFin && <span className="text-xs text-text-secondary">Vigencia hasta {new Date(c.vigenciaFin).toLocaleDateString('es-MX')}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary"><Pencil size={14} /></button>
                <button onClick={() => deleteMutation.mutate(c.id)} disabled={deleteMutation.isPending} className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB mobile */}
      <button
        onClick={openCreate}
        className="md:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg flex items-center justify-center"
        style={{ boxShadow: '0 4px 16px rgba(14,133,191,0.4)' }}
        aria-label="Nuevo convenio"
      >
        <Plus size={24} strokeWidth={2.5} />
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex flex-col justify-end md:justify-center md:items-center md:p-4">
          <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full md:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 md:hidden">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="font-semibold text-text-primary">{editing ? 'Editar convenio' : 'Nuevo convenio'}</h3>
              <button onClick={closeModal} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary"><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Nombre *</label>
                <input required className={inputCls} value={form.nombre} onChange={f('nombre')} placeholder="Convenio IMSS" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Tipo *</label>
                  <select required className={inputCls} value={form.tipo} onChange={f('tipo')}>
                    {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Entidad</label>
                  <input className={inputCls} value={form.entidad} onChange={f('entidad')} placeholder="IMSS, ISSSTE..." />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Descuento (%)</label>
                <input type="number" min={0} max={100} step="0.01" className={inputCls} value={form.descuentoPorcentaje} onChange={f('descuentoPorcentaje')} placeholder="0.00" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Vigencia inicio</label>
                  <input type="date" className={inputCls} value={form.vigenciaInicio} onChange={f('vigenciaInicio')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Vigencia fin</label>
                  <input type="date" className={inputCls} value={form.vigenciaFin} onChange={f('vigenciaFin')} />
                </div>
              </div>
              {editing && (
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="activo" checked={form.activo} onChange={e => setForm(p => ({ ...p, activo: e.target.checked }))} />
                  <label htmlFor="activo" className="text-sm text-text-primary">Convenio activo</label>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
                  {editing ? 'Guardar cambios' : 'Crear convenio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
